// Video Analytics Service
// Tracks video views, timecode events, and player interactions

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate unique session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('video_analytics_session');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    sessionStorage.setItem('video_analytics_session', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const width = window.innerWidth;
  if (width <= 768) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
};

// Get browser info
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'unknown';
  
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';
  else if (ua.includes('Opera')) browser = 'Opera';
  
  return browser;
};

// Get OS info
const getOSInfo = () => {
  const ua = navigator.userAgent;
  let os = 'unknown';
  
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  return os;
};

// Send analytics event (fire and forget)
const sendEvent = async (endpoint, data) => {
  try {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    await fetch(`${API}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      keepalive: true // Allows request to complete even after page unload
    });
  } catch (e) {
    // Silent fail - analytics should not break the app
    console.debug('Analytics event failed:', e);
  }
};

// Current view state
let currentViewId = null;
let currentGameId = null;
let viewStartTime = null;
let lastProgressTime = 0;
let progressInterval = null;

// ===== VIDEO VIEW TRACKING =====

export const trackVideoStart = async (gameId, sourcePage = 'game_page', playerMode = 'embedded') => {
  currentGameId = gameId;
  viewStartTime = Date.now();
  
  const data = {
    game_id: gameId,
    session_id: getSessionId(),
    source_page: sourcePage,
    player_mode: playerMode,
    device_type: getDeviceType(),
    browser: getBrowserInfo(),
    os: getOSInfo(),
    screen_resolution: `${window.innerWidth}x${window.innerHeight}`
  };
  
  try {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await fetch(`${API}/analytics/video/start`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    
    if (response.ok) {
      const result = await response.json();
      currentViewId = result.view_id;
      
      // Start progress tracking (every 10 seconds)
      if (progressInterval) clearInterval(progressInterval);
      progressInterval = setInterval(() => {
        if (currentViewId && lastProgressTime > 0) {
          trackVideoProgress(lastProgressTime);
        }
      }, 10000);
      
      return result.view_id;
    }
  } catch (e) {
    console.debug('trackVideoStart failed:', e);
  }
  
  return null;
};

export const trackVideoProgress = (currentTime, quality = null) => {
  if (!currentViewId) return;
  
  lastProgressTime = currentTime;
  
  sendEvent('/analytics/video/progress', {
    view_id: currentViewId,
    current_time: currentTime,
    quality: quality
  });
};

export const trackVideoEnd = (duration, completed = false, reason = 'closed') => {
  if (!currentViewId) return;
  
  // Stop progress tracking
  if (progressInterval) {
    clearInterval(progressInterval);
    progressInterval = null;
  }
  
  const durationWatched = viewStartTime ? (Date.now() - viewStartTime) / 1000 : 0;
  const completionPercent = duration > 0 ? (lastProgressTime / duration) * 100 : 0;
  
  sendEvent('/analytics/video/end', {
    view_id: currentViewId,
    duration_watched: Math.min(durationWatched, lastProgressTime),
    completed: completed,
    completion_percent: Math.min(completionPercent, 100),
    end_reason: reason
  });
  
  // Reset state
  currentViewId = null;
  currentGameId = null;
  viewStartTime = null;
  lastProgressTime = 0;
};

// ===== TIMECODE TRACKING =====

export const trackTimecodeTriggered = (gameId, panoramaId, timecode, videoTime, triggerType = 'auto') => {
  sendEvent('/analytics/timecode/triggered', {
    game_id: gameId,
    view_id: currentViewId,
    session_id: getSessionId(),
    panorama_id: panoramaId,
    timecode: timecode,
    trigger_type: triggerType,
    video_time: videoTime
  });
};

// Track when user leaves panorama
export const trackPanoramaExit = (gameId, panoramaId, timeSpent, exitAction, returnedToVideo = false) => {
  sendEvent('/analytics/panorama/exit', {
    game_id: gameId,
    session_id: getSessionId(),
    panorama_id: panoramaId,
    time_spent: timeSpent,
    exit_action: exitAction,
    returned_to_video: returnedToVideo
  });
};

// ===== INTERACTION TRACKING =====

export const trackInteraction = (gameId, interactionType, playerMode = 'mini', videoTime = 0, details = null) => {
  sendEvent('/analytics/interaction', {
    game_id: gameId,
    view_id: currentViewId,
    session_id: getSessionId(),
    interaction_type: interactionType,
    player_mode: playerMode,
    video_time: videoTime,
    details: details
  });
};

// Convenience methods for common interactions
export const trackCountdownClick = (gameId, videoTime, clickAtSecond) => {
  trackInteraction(gameId, 'countdown_click', 'mini', videoTime, { click_at_second: clickAtSecond });
};

export const trackCountdownWait = (gameId, videoTime) => {
  trackInteraction(gameId, 'countdown_wait', 'mini', videoTime);
};

export const trackMiniplayerShow = (gameId, videoTime) => {
  trackInteraction(gameId, 'miniplayer_show', 'mini', videoTime);
};

export const trackMiniplayerHide = (gameId, videoTime) => {
  trackInteraction(gameId, 'miniplayer_hide', 'mini', videoTime);
};

export const trackMiniplayerClose = (gameId, videoTime) => {
  trackInteraction(gameId, 'miniplayer_close', 'mini', videoTime);
};

export const trackMiniplayerDrag = (gameId, videoTime) => {
  trackInteraction(gameId, 'miniplayer_drag', 'mini', videoTime);
};

export const trackFullscreenEnter = (gameId, videoTime) => {
  trackInteraction(gameId, 'fullscreen_enter', 'fullscreen', videoTime);
};

export const trackFullscreenExit = (gameId, videoTime) => {
  trackInteraction(gameId, 'fullscreen_exit', 'mini', videoTime);
};

export const trackQualityChange = (gameId, videoTime, newQuality) => {
  trackInteraction(gameId, 'quality_change', 'unknown', videoTime, { quality: newQuality });
};

export const trackSeek = (gameId, videoTime, seekTo) => {
  trackInteraction(gameId, 'seek', 'unknown', videoTime, { seek_to: seekTo });
};

export const trackPlayPause = (gameId, videoTime, isPlaying) => {
  trackInteraction(gameId, 'play_pause', 'unknown', videoTime, { is_playing: isPlaying });
};

// Get current view ID (for external use)
export const getCurrentViewId = () => currentViewId;
export const getCurrentGameId = () => currentGameId;

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (currentViewId) {
      trackVideoEnd(0, false, 'navigated_away');
    }
  });
}

export default {
  trackVideoStart,
  trackVideoProgress,
  trackVideoEnd,
  trackTimecodeTriggered,
  trackPanoramaExit,
  trackInteraction,
  trackCountdownClick,
  trackCountdownWait,
  trackMiniplayerShow,
  trackMiniplayerHide,
  trackMiniplayerClose,
  trackMiniplayerDrag,
  trackFullscreenEnter,
  trackFullscreenExit,
  trackQualityChange,
  trackSeek,
  trackPlayPause,
  getCurrentViewId,
  getCurrentGameId
};
